import React, { useState } from 'react';
import axios from 'axios';
import { Droplet, CheckCircle, AlertCircle, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OilAndGasContact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    phone: '',
    projectLocation: '',
    operationType: '',
    facilityType: '',
    productionCapacity: '',
    emissionSources: '',
    voc_emissions: '',
    flaring: '',
    storageTypes: '',
    existingPermits: '',
    complianceHistory: '',
    timeline: '',
    specificRequirements: '',
    additionalInfo: '',
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${API}/contact/oil-and-gas`, formData);
      
      if (response.status === 200) {
        setStatus({
          type: 'success',
          message: 'Thank you! We will review your oil & gas project details and contact you within 1-2 business days.',
        });
        setFormData({
          firstName: '',
          lastName: '',
          company: '',
          email: '',
          phone: '',
          projectLocation: '',
          operationType: '',
          facilityType: '',
          productionCapacity: '',
          emissionSources: '',
          voc_emissions: '',
          flaring: '',
          storageTypes: '',
          existingPermits: '',
          complianceHistory: '',
          timeline: '',
          specificRequirements: '',
          additionalInfo: '',
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'There was an error submitting your inquiry. Please try again or call us directly at (907) 687-8263.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-amber-900 to-amber-700 px-8 py-12 text-white">
            <div className="flex items-center mb-4">
              <Droplet className="w-12 h-12 mr-4" />
              <div>
                <h1 className="text-3xl font-bold">Oil & Gas Project Inquiry</h1>
                <p className="text-amber-100 mt-2">Tell us about your oil & gas operation needs</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            {status.message && (
              <div className={`mb-6 p-4 rounded-lg flex items-start space-x-3 ${status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                {status.type === 'success' ? <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                <p>{status.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name <span className="text-red-500">*</span></label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name <span className="text-red-500">*</span></label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company <span className="text-red-500">*</span></label>
                    <input type="text" name="company" value={formData.company} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone <span className="text-red-500">*</span></label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email <span className="text-red-500">*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Project Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Location</label>
                    <input type="text" name="projectLocation" value={formData.projectLocation} onChange={handleChange} placeholder="e.g., North Slope, Cook Inlet" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Operation Type</label>
                    <select name="operationType" value={formData.operationType} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500">
                      <option value="">Select operation type</option>
                      <option value="exploration">Exploration & Drilling</option>
                      <option value="production">Production Operations</option>
                      <option value="transmission">Transmission & Storage</option>
                      <option value="processing">Processing Facilities</option>
                      <option value="gathering">Gathering Systems</option>
                      <option value="portable">Portable Operations (POGO)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Facility Type</label>
                    <select name="facilityType" value={formData.facilityType} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500">
                      <option value="">Select facility type</option>
                      <option value="production">Production Facility</option>
                      <option value="processing">Processing Plant</option>
                      <option value="compressor">Compressor Station</option>
                      <option value="storage">Storage Terminal</option>
                      <option value="pipeline">Pipeline Facility</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Production Capacity</label>
                    <input type="text" name="productionCapacity" value={formData.productionCapacity} onChange={handleChange} placeholder="e.g., 10,000 bbl/day, 50 MMcf/day" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Emission Sources</label>
                    <input type="text" name="emissionSources" value={formData.emissionSources} onChange={handleChange} placeholder="e.g., engines, heaters, dehydrators, flares" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">VOC Emissions</label>
                    <input type="text" name="voc_emissions" value={formData.voc_emissions} onChange={handleChange} placeholder="Expected VOC emissions level" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Flaring Operations</label>
                    <input type="text" name="flaring" value={formData.flaring} onChange={handleChange} placeholder="Describe flaring operations" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Storage Types</label>
                    <input type="text" name="storageTypes" value={formData.storageTypes} onChange={handleChange} placeholder="e.g., crude oil tanks, condensate tanks" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Existing Permits</label>
                    <select name="existingPermits" value={formData.existingPermits} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500">
                      <option value="">Select permit status</option>
                      <option value="no_permit">No Permit (New Project)</option>
                      <option value="planning">Planning/Pre-Construction</option>
                      <option value="existing">Have Existing Permit</option>
                      <option value="modification">Permit Modification Needed</option>
                      <option value="mg2">Need MG2 (POGO) Permit</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Compliance History</label>
                    <textarea name="complianceHistory" value={formData.complianceHistory} onChange={handleChange} rows="2" placeholder="Any compliance issues or violations" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Timeline</label>
                    <select name="timeline" value={formData.timeline} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500">
                      <option value="">Select timeline</option>
                      <option value="immediate">Immediate (within 2 weeks)</option>
                      <option value="1_month">Within 1 month</option>
                      <option value="1-3_months">1-3 months</option>
                      <option value="3-6_months">3-6 months</option>
                      <option value="6+_months">6+ months</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specific Requirements or Concerns <span className="text-red-500">*</span></label>
                    <textarea name="specificRequirements" value={formData.specificRequirements} onChange={handleChange} required rows="4" placeholder="e.g., Need BACT analysis, air quality modeling, emission inventory, NSPS compliance, etc." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                    <textarea name="additionalInfo" value={formData.additionalInfo} onChange={handleChange} rows="3" placeholder="Any other details about your project..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"></textarea>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={isSubmitting} className="w-full bg-amber-700 text-white py-4 px-6 rounded-lg font-semibold hover:bg-amber-800 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? <span>Submitting...</span> : <><span>Submit Oil & Gas Inquiry</span><Send size={20} /></>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OilAndGasContact;
